import { map, Observable, OperatorFunction } from 'rxjs';
import DOMPurify from 'dompurify';

/**
 * Begründung: Dies ist lediglich eine Utilsklasse für Methoden die Dates betreffen.
 */
export class DateUtils {
  public static isToday(date: Date | null | undefined): boolean | null {
    if (!date) return null;
    const currentDate = new Date();
    return (
      date.getDate() == currentDate.getDate() &&
      date.getMonth() == currentDate.getMonth() &&
      date.getFullYear() == currentDate.getFullYear()
    );
  }
}

export function format(template: string, ...args: string[]): string {
  return template.replaceAll(/{(\d+)}/g, (match, index) => args[index] || '');
}

export function withPrevious<T>(signal: () => T): () => { previous: T | undefined; current: T } {
  let prev: T | undefined;

  return () => {
    const current = signal();
    const previous = prev;
    prev = current;
    return { previous, current };
  };
}

/**
 * Transforms an Observable of Map<K, V> into an Observable of Map<K, R>,
 * applying the given projector function to each entry.
 */
export function mapMap<K, V, R>(
  source$: Observable<Map<K, V>>,
  projector: (value: V, key: K) => R
): Observable<Map<K, R>> {
  return source$.pipe(
    map(originalMap => {
      const result = new Map<K, R>();
      for (const [key, value] of originalMap.entries()) {
        result.set(key, projector(value, key));
      }
      return result;
    })
  );
}

export function mapMapOp<K, V, R>(projector: (value: V, key: K) => R): OperatorFunction<Map<K, V>, Map<K, R>> {
  return map(originalMap => {
    const result = new Map<K, R>();
    for (const [key, value] of originalMap.entries()) {
      result.set(key, projector(value, key));
    }
    return result;
  });
}

// universeller Helfer — nimmt MatrixEvent oder rohes Event-Objekt
export function getUserIdFromEvent(ev: any): string | undefined {
  if (!ev) return undefined;

  // 1) MatrixEvent-Wrapper (empfohlen): hat getSender()
  if (typeof ev.getSender === 'function') {
    const byGetter = ev.getSender();
    if (byGetter) return byGetter;
    // fallback: raw JSON inside wrapper
    if (ev.event?.sender) return ev.event.sender;
    // ev.sender kann ein RoomMember-Objekt sein -> userId prüfen
    if (ev.sender?.userId) return ev.sender.userId;
  }

  // 2) Rohes Event-JSON (IEvent): .sender ist ein string
  if (typeof ev.sender === 'string') return ev.sender;
  if (ev.event?.sender && typeof ev.event.sender === 'string') return ev.event.sender;

  return undefined;
}

export async function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function byteToMBString(byte: number): string {
  return (byte / (1024 * 1024)).toFixed(2);
}

/**
 * Helferfunktion: Prüft, ob alle Schlüssel von obj1 in obj2 vorhanden sind
 * @param obj1
 * @param obj2
 * @returns boolean
 */
export function hasObj2AllKeysOfObj1(obj1: object, obj2: object): boolean {
  let hasAllKeys = true;

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  keys1.forEach(it => {
    if (!keys2.includes(it)) {
      hasAllKeys = false;
    }
  });
  return hasAllKeys;
}

export function sanitizeMatrixHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p',
      'br',
      'strong',
      'em',
      'u',
      's',
      'a',
      'blockquote',
      'code',
      'pre',
      'ul',
      'ol',
      'li',
      'span',
      'mx-reply',
      'img'
    ],
    ALLOWED_ATTR: ['href', 'title', 'class', 'data-*', 'src', 'alt'],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|matrix|mxc):|mailto:|#)/i
  });
}

export async function copyToClipboard(
  text: string,
  onSuccess?: (message?: string) => void,
  onError?: (message?: string) => void,
  successMessage?: string,
  errorMessage?: string
): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    onSuccess?.(successMessage);
  } catch (err) {
    console.error('Error copying to clipboard:', err);
    onError?.(errorMessage);
  }
}

export function preventInvalidNumberInput(event: KeyboardEvent): void {
  const allowedKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End'
  ];

  if (allowedKeys.includes(event.key) || /^[0-9]$/.test(event.key)) {
    return;
  }

  event.preventDefault();
}
